/**
 * 后台路由。
 *
 * 组织方式：布局路由 `/admin` → 子路由即左侧菜单项（item）。
 * **菜单由这份定义 + 当前管理员 permissions 动态生成**：
 * 服务端没给某个权限码，对应菜单就不渲染、路由也进不去。
 *
 * ⚠️ 前端隐藏菜单不等于安全：后端每个接口都会二次校验权限。
 */
import { createRouter, createWebHashHistory, type RouteRecordRaw } from 'vue-router'
import { PERM } from '@/utils/permission'

declare module 'vue-router' {
  interface RouteMeta {
    /** 页面标题 */
    title?: string
    /** 所需权限码（单个或数组，数组需全部满足） */
    permission?: string | string[]
    permissions?: string[]
    /** 是否在侧边菜单中展示 */
    hidden?: boolean
    /** 菜单图标（Element Plus 图标组件名） */
    icon?: string
    /** 不需要登录 */
    publicRoute?: boolean
    /** 是否作为菜单分组标题（不可点击） */
    group?: string
    /** 高亮的父菜单路径（详情页用） */
    activeMenu?: string
  }
}

/** 详情页/编辑页不占菜单，但需要权限 */
const DETAIL_HIDDEN_ROUTES: RouteRecordRaw[] = []

export const ADMIN_LAYOUT_ROUTE_NAME = 'AdminLayout'

export const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/admin/dashboard',
  },
  {
    path: '/admin/login',
    name: 'AdminLogin',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { title: '登录', publicRoute: true, hidden: true },
  },
  {
    path: '/admin',
    name: ADMIN_LAYOUT_ROUTE_NAME,
    component: () => import('@/layouts/AdminLayout.vue'),
    redirect: '/admin/dashboard',
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/views/dashboard/DashboardView.vue'),
        meta: { title: '工作台', icon: 'Odometer', permission: PERM.DASHBOARD_VIEW },
      },
      {
        path: 'product',
        name: 'ProductList',
        component: () => import('@/views/product/ProductListView.vue'),
        meta: { title: '商品列表', icon: 'Goods', permission: PERM.PRODUCT_VIEW },
      },
      {
        path: 'product/edit',
        name: 'ProductEdit',
        component: () => import('@/views/product/ProductEditView.vue'),
        meta: {
          title: '商品编辑',
          hidden: true,
          permission: PERM.PRODUCT_EDIT,
          activeMenu: '/admin/product',
        },
      },
      {
        path: 'category',
        name: 'CategoryManage',
        component: () => import('@/views/product/CategoryView.vue'),
        meta: { title: '分类管理', icon: 'Menu', permission: PERM.CATEGORY_VIEW },
      },
      {
        /**
         * A-30 活动管理（「分类」改成「活动列表」需求的后台面）。
         *
         * C 端底部导航的「分类」tab 已换成「活动」列表，页面的内容就是这里配的。
         *
         * ⚠️ 权限码 `activity:view` / `activity:edit` **必须同时存在于**
         *    后端 AdminPermission、前端 PERM 与 PERMISSION_TREE_FALLBACK 三处，
         *    否则运营在角色界面勾不到它，非超管永远进不了这一页
         *    （`__tests__/permission-contract.spec.ts` 会守住这条）。
         */
        path: 'activity',
        name: 'ActivityManage',
        component: () => import('@/views/marketing/ActivityView.vue'),
        meta: { title: '活动管理', icon: 'Calendar', permission: PERM.ACTIVITY_VIEW },
      },
      {
        path: 'order',
        name: 'OrderList',
        component: () => import('@/views/order/OrderListView.vue'),
        meta: { title: '订单列表', icon: 'List', permission: PERM.ORDER_VIEW },
      },
      {
        path: 'order/batch-ship',
        name: 'OrderBatchShip',
        component: () => import('@/views/order/BatchShipView.vue'),
        meta: {
          title: '批量发货',
          icon: 'Van',
          permission: PERM.ORDER_SHIP,
          activeMenu: '/admin/order',
        },
      },
      {
        path: 'order/:id',
        name: 'OrderDetail',
        component: () => import('@/views/order/OrderDetailView.vue'),
        meta: {
          title: '订单详情',
          hidden: true,
          permission: PERM.ORDER_VIEW,
          activeMenu: '/admin/order',
        },
      },
      {
        path: 'refund',
        name: 'RefundList',
        component: () => import('@/views/refund/RefundListView.vue'),
        meta: { title: '售后列表', icon: 'RefreshLeft', permission: PERM.REFUND_VIEW },
      },
      {
        path: 'refund/:id',
        name: 'RefundDetail',
        component: () => import('@/views/refund/RefundDetailView.vue'),
        meta: {
          title: '售后详情与审核',
          hidden: true,
          permission: PERM.REFUND_VIEW,
          activeMenu: '/admin/refund',
        },
      },
      {
        path: 'member',
        name: 'MemberList',
        component: () => import('@/views/member/MemberListView.vue'),
        meta: { title: '会员列表', icon: 'User', permission: PERM.MEMBER_VIEW },
      },
      {
        path: 'member/merge',
        name: 'MemberMerge',
        component: () => import('@/views/member/MemberMergeView.vue'),
        meta: {
          title: '账号合并',
          icon: 'Connection',
          permission: PERM.MEMBER_MERGE,
          activeMenu: '/admin/member',
        },
      },
      {
        /**
         * A-29 VIP 升级码：这里发码 / 作废（注册不再需要邀请码，码用于 C 端「我的 → VIP 升级」）。
         *
         * ⚠️ 路径刻意用**顶层 `invite`** 而不是 `member/invite-code`：
         *    同级已有 `member/:id` 动态路由，把新页面挂在 `member/` 下会依赖
         *    "静态段必须声明在动态段之前"这一隐式约定 —— 以后有人调整顺序，
         *    页面就会被 `member/:id` 抢走（表现为"点菜单进了会员详情"）。
         */
        path: 'invite',
        name: 'InviteCodeManage',
        component: () => import('@/views/member/InviteCodeView.vue'),
        meta: { title: 'VIP 升级码', icon: 'Ticket', permission: PERM.INVITE_VIEW },
      },
      {
        path: 'member/:id',
        name: 'MemberDetail',
        component: () => import('@/views/member/MemberDetailView.vue'),
        meta: {
          title: '会员详情',
          hidden: true,
          permission: PERM.MEMBER_VIEW,
          activeMenu: '/admin/member',
        },
      },
      {
        path: 'level',
        name: 'LevelConfig',
        component: () => import('@/views/member/LevelConfigView.vue'),
        meta: { title: '等级配置', icon: 'Medal', permission: PERM.LEVEL_VIEW },
      },
      {
        path: 'points-rule',
        name: 'PointsRule',
        component: () => import('@/views/member/PointsRuleView.vue'),
        meta: { title: '积分规则', icon: 'Coin', permission: PERM.POINTS_RULE_VIEW },
      },
      {
        path: 'coupon',
        name: 'CouponList',
        component: () => import('@/views/marketing/CouponListView.vue'),
        meta: { title: '优惠券列表', icon: 'Ticket', permission: PERM.COUPON_VIEW },
      },
      {
        path: 'coupon/edit',
        name: 'CouponEdit',
        component: () => import('@/views/marketing/CouponEditView.vue'),
        meta: {
          title: '优惠券编辑',
          hidden: true,
          permission: PERM.COUPON_EDIT,
          activeMenu: '/admin/coupon',
        },
      },
      {
        path: 'freight',
        name: 'FreightTemplate',
        component: () => import('@/views/marketing/FreightView.vue'),
        meta: { title: '运费模板', icon: 'Box', permission: PERM.FREIGHT_VIEW },
      },
      {
        path: 'pay-channel',
        name: 'PayChannelConfig',
        component: () => import('@/views/pay/PayChannelView.vue'),
        meta: { title: '收银台配置', icon: 'CreditCard', permission: PERM.PAYCHANNEL_VIEW },
      },
      {
        // A20b 快递100 电子面单设置：发货人 / 默认内件 / 面单模板 + 连通性自检。
        // 权限用 express:view（查看）——保存另需 express:setting-edit，在页面内单独判定。
        path: 'express',
        name: 'ExpressSettings',
        component: () => import('@/views/pay/ExpressSettingsView.vue'),
        meta: { title: '快递100 设置', icon: 'Van', permission: PERM.EXPRESS_VIEW },
      },
      {
        path: 'reconcile',
        name: 'ReconcileManage',
        component: () => import('@/views/pay/ReconcileView.vue'),
        meta: { title: '对账管理', icon: 'Money', permission: PERM.RECONCILE_VIEW },
      },
      {
        path: 'page/home',
        name: 'PageHome',
        component: () => import('@/views/page/HomeDecorationView.vue'),
        meta: { title: '首页装修', icon: 'Layout', permission: PERM.PAGE_VIEW },
      },
      {
        // A23 Banner 管理：轮播图已纳入「首页装修」统一管理（同一份 banner 表、同一个页面），
        // 这里指向同一个组件而不是再写一份 —— 两个入口编辑同一份数据，
        // 迟早会出现"这页改了那页没刷"的困惑。
        // ⚠️ 必须是 component 而不是 redirect：菜单按「有无 component」过滤
        //    （见 router/menu.ts 的 isMenuRoute），只写 redirect 会让这个菜单项静默消失。
        path: 'banner',
        name: 'BannerManage',
        component: () => import('@/views/page/HomeDecorationView.vue'),
        meta: { title: 'Banner 管理', icon: 'Picture', permission: PERM.BANNER_VIEW },
      },
      {
        path: 'content',
        name: 'ContentManage',
        // ★ 2026-09-29：从占位页换成真实页面 —— 帮助中心文章 / 协议 / 首页公告都能在后台改了
        component: () => import('@/views/content/ContentView.vue'),
        meta: { title: '内容管理', icon: 'Document', permission: PERM.CONTENT_VIEW },
      },
      {
        path: 'setting',
        name: 'SystemSetting',
        component: () => import('@/views/system/SettingView.vue'),
        meta: { title: '系统设置', icon: 'Setting', permission: PERM.SETTING_VIEW },
      },
      {
        path: 'admin-user',
        name: 'AdminUserManage',
        component: () => import('@/views/system/AdminUserView.vue'),
        meta: { title: '管理员账号', icon: 'Avatar', permission: PERM.ADMINUSER_VIEW },
      },
      {
        path: 'role',
        name: 'RoleManage',
        component: () => import('@/views/system/RoleView.vue'),
        meta: { title: '角色权限', icon: 'Key', permission: PERM.ROLE_VIEW },
      },
      {
        path: 'log',
        name: 'AuditLog',
        component: () => import('@/views/system/AuditLogView.vue'),
        meta: { title: '审计日志', icon: 'Files', permission: PERM.LOG_VIEW },
      },
      ...DETAIL_HIDDEN_ROUTES,
      {
        path: '403',
        name: 'AdminForbidden',
        component: () => import('@/views/error/ForbiddenView.vue'),
        meta: { title: '没有权限', hidden: true },
      },
      {
        path: ':pathMatch(.*)*',
        name: 'AdminNotFound',
        component: () => import('@/views/error/NotFoundView.vue'),
        meta: { title: '页面不存在', hidden: true },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/admin/dashboard',
  },
]

export const router = createRouter({
  // hash 路由：后台部署在子路径/静态服务器上都不需要额外 rewrite 配置
  history: createWebHashHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
})

export default router
